import $ from 'jquery';

import Autocomplete from 'chairisher/component/autocomplete';
import KeyUtils from 'chairisher/util/key';
import ModalViewHelper from 'chairisher/view/helper/modal';
import NavigationView from 'chairisher/view/navigation';
import ViewportUtil from 'chairisher/util/viewport';

import trackGlobalSearch from 'chairisher/analytics/globalsearch';

import { getSearchAutocompleteEndpoint, getMinTypeaheadTokenLength } from 'chairisher/context/search';
import { isMotdBannerVisible, getScrollY } from 'chairisher/context/site';
import { isMaxMediumDesktop, isMinLargeDesktop } from 'chairisher/util/mediaquery';

/**
 * View that encapsulates functionality related to the global search bar
 *
 * @param {Object} settings
 * @param {jQuery=} settings.$menuContainerEl @see this.$menuContainerEl
 * @param {jQuery} settings.$queryEl jQuery element representing the text box input
 * @param {string=} settings.dataEndPoint The endpoint to hit for autocompletion suggestions; default is `search_lookup`
 * @param {number=} settings.limit The number of suggestions that the server should return; default is 12
 * @constructor
 */
const GlobalSearchView = function GlobalSearchView(settings) {
    const options = $.extend(
        {
            $menuContainerEl: this.$menuContainerEl,
            $cancelEl: this.$cancelEl,
            $queryEl: this.$queryEl,
            dataEndpoint: this.dataEndpoint,
            dataObject: { global: true },
            isGlobalSearch: true,
            limit: this.limit,
            minQueryLength: getMinTypeaheadTokenLength(),
        },
        settings,
    );

    options.menuElClasses = 'menu';
    if (isMaxMediumDesktop() && isMotdBannerVisible()) {
        options.menuElClasses += ' banner-visible';
    }

    options.$menuContainerEl = function getMenuContainerEl() {
        if (isMinLargeDesktop()) {
            return this.$el; // `this` points to the autocompleter
        }
        return $('#js-global-search-menu-container');
    };

    $('#js-global-nav').after(
        $('<div></div>', {
            class: 'global-search-menu-container',
            id: 'js-global-search-menu-container',
        }),
    );

    this.autocomplete = new Autocomplete(options);
    this.$cancelEl = options.$cancelEl;
    this.$queryEl = this.autocomplete.getQueryEl();
};

/**
 * jQuery element representing the search cancel button
 *
 * @type {jQuery}
 */
GlobalSearchView.prototype.$cancelEl = null;

/**
 * Optional jQuery element to append the menu to; if a function, it must return a jQuery object
 *
 * @type {jQuery|function}
 */
GlobalSearchView.prototype.$menuContainerEl = null;

/**
 * jQuery element representing the text box input
 *
 * @type {jQuery}
 */
GlobalSearchView.prototype.$queryEl = null;

/**
 * The Autocomplete instance tied to `this.$queryEl`
 *
 * @type {Autocomplete}
 */
GlobalSearchView.prototype.autocomplete = null;

/**
 * The endpoint to hit for autocompletion suggestions
 *
 * @type {string}
 * @default
 */
GlobalSearchView.prototype.dataEndpoint = getSearchAutocompleteEndpoint();

/**
 * The number of suggestions that the server should return
 *
 * @type {number}
 * @default
 */
GlobalSearchView.prototype.limit = 12;

/**
 * Binds various actions to the global search view
 */
GlobalSearchView.prototype.bind = function bind() {
    this.$queryEl.on(
        'autocomplete:blur',
        $.proxy(() => {
            ViewportUtil.unlockViewport();
        }, this),
    );

    this.$queryEl.on(
        'autocomplete:focus',
        $.proxy(function onAutocompleteFocus() {
            this.toggleProductNav(false);

            if (getScrollY() === 0 && isMaxMediumDesktop()) {
                $('html, body').animate(
                    {
                        scrollTop: $('#js-promo-banner').height(),
                    },
                    $.proxy(function onPromoBannerScrollComplete() {
                        this.toggleProductNav(false);
                    }, this),
                );
            }

            if (this.autocomplete.canMakeQuery()) {
                this.autocomplete.fetchData();
            }

            if (this.autocomplete.hasQueryText()) {
                if (this.autocomplete.hasChoices()) {
                    this.autocomplete.getMenu().show();
                }
            }
        }, this),
    );

    this.$queryEl.on('autocomplete:keypress', (e) => {
        if (e.which === KeyUtils.KeyCodes.Enter) {
            e.preventDefault();

            const query = this.autocomplete.getQueryText() || '';

            trackGlobalSearch('masthead', query);
            $(e.currentTarget).closest('form').submit();
        }
    });

    this.$queryEl.on(
        'keyup',
        $.proxy(function onQueryKeyup(e) {
            window.clearTimeout(this.typingTimeout);

            if (KeyUtils.KeyCodes.Escape === e.which) {
                ModalViewHelper.toggleBackdrop(false);
            }

            if (!KeyUtils.isEnter(e.which)) {
                this.typingTimeout = window.setTimeout(
                    $.proxy(function onQueryTypingPause() {
                        const menu = this.autocomplete.getMenu();
                        const canMakeQuery = this.autocomplete.canMakeQuery();

                        NavigationView.toggleGlobalSearchSuggestions(canMakeQuery);

                        if (canMakeQuery) {
                            NavigationView.toggleProductNav(false);
                            ViewportUtil.lockViewport();
                        } else {
                            ViewportUtil.unlockViewport();
                        }

                        menu.toggle(canMakeQuery);
                    }, this),
                    100,
                );
            }
        }, this),
    );

    this.$cancelEl.on(
        'click',
        $.proxy(function onSearchCancelClick() {
            this.autocomplete.setQueryText('');
            this.autocomplete.getMenu().updateSuggestionChoices([]);
            NavigationView.toggleGlobalSearchSuggestions(false);
            ModalViewHelper.toggleBackdrop(false);
        }, this),
    );

    this.$queryEl.on('autocomplete:selected', (_, item) => {
        trackGlobalSearch('masthead', null, item.display || item.additional_display, item.url);
        window.location.href = item.url; // open in current window
    });

    $('#js-mobile-nav-trigger').on(
        'click',
        $.proxy(function onMobileNavTriggerClick() {
            this.autocomplete.setQueryText('');
            this.autocomplete.hidePlaceholder();
            this.autocomplete.getMenu().updateSuggestionChoices([]);
        }, this),
    );

    $('.js-submit-icon').on('click', () => {
        trackGlobalSearch('masthead', this.autocomplete.getQueryText());
    });
};

GlobalSearchView.prototype.toggleProductNav = function toggleProductNav(shouldDisplayProductNav) {
    NavigationView.toggleGlobalSearchSuggestions(!shouldDisplayProductNav);
    NavigationView.toggleProductNav(shouldDisplayProductNav);
};

export default GlobalSearchView;
